import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { getStoreId } from '@utils/EnvironmentUtil'
import {
    CountMetric,
    LatencyMetric,
    AudioException,
    HigherPriorityAudioPlayingException,
    InterruptedAudioException,
    UnsupportedBrowserException,
} from '@amzn/ihm-associate-tools-js-lib'

export const AudioPriority = {
    DEFAULT: 0,
    NOTIFICATION: 1,
    VOICE: 2,
    VOICE_AUDIO_CUE: 3, // Make sure we interrupt VOICE
}

let audioSingleton
let current

/**
 * Plays the audio from the provided URL only if there is nothing playing or if the priority of the request
 * is less then or equal to the audio that is currently playing. Returns a promise that will resolve when the
 * audio in done playing and will reject if there are any errors, if the priority is less than the audio that
 * is currently playing, or if the audio is overriden by a higher priority request.
 *
 * Note that this could fail if the user hasn't interacted enough with the web page.
 *  See: https://developers.google.com/web/updates/2017/09/autoplay-policy-changes
 *
 * @param {string} url - URL of the audio file to play
 * @param {object} config - config object containing the priority (this is optional). Example
 *  {
 *      priority: {number} - priority number for this request. (Optional. Defaults to AudioPriority.MIN)
 *  }
 * @return {Promise} - Promise that gets resolved once the audio is done playing or rejects if there's an error
 *  or a higher priority audio is either playing or about to be played.
 */
export const playSoundBySource = (url, config) => {
    if (window.Audio) {
        audioSingleton = new window.Audio()
    }

    activate()

    const unsupportedMetric = new CountMetric(getStoreId(), 'AudioUnsupported', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
    if (!audioSingleton) {
        console.warn('window.Audio is not available in this browser instance.')

        unsupportedMetric.count(1)
        return Promise.reject(new UnsupportedBrowserException({
            message: 'Audio is not supported',
        }))
    }
    unsupportedMetric.count(0)

    config = config || {}

    // Verify the priority of the incoming request and compare it against the priority of the request
    // that is currently playing. Only accept requests that have the priority greater than or equal
    // to the current playing priority.
    const priority = config.priority || AudioPriority.DEFAULT

    const audioOverriddenMetric = new CountMetric(getStoreId(), 'AudioOverridden', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
    const higherPriorityMetric = new CountMetric(getStoreId(), 'HigherPriorityAudioPlaying', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
    if (current && current.priority > priority) {
        audioOverriddenMetric.count(0)
        higherPriorityMetric.count(1)
        return Promise.reject(new HigherPriorityAudioPlayingException({
            message: 'A higher priority audio is playing',
        }))
    }

    higherPriorityMetric.count(0)

    clearCurrentAndEmitMetricIfNecessary()

    const localCurrent = {
        deferred: generateDeferredPromise(),
        priority,
    }
    current = localCurrent

    // Track the amount of time it takes from when we set the src (the download starts) to when
    // we begin playing
    const audioFirstByteLatencyMetric = new LatencyMetric(getStoreId(), 'TimeToFirstByte', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
    audioFirstByteLatencyMetric.start()
    audioSingleton.src = url
    play()
    .then(() => {
        audioFirstByteLatencyMetric.stop()
    })
    .catch((error) => {
        new CountMetric(getStoreId(), 'PlayError', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
            .count(1)
        const rejectMessage = generateBrowserRejectionAndEmitMetrics(error)

        // This looks a bit weird, but basically, between the moment that we created the deferred
        // and the moment we get here, another call to playSoundBySource could have happened. That
        // would have gone into the clearCurrentAndEmitMetricIfNecessary  and would have rejected this
        // promise already. If we don't make this check, we could end up rejecting a promise that
        // won't fail.
        if (localCurrent === current) {
            console.debug('Rejecting current', error)
            rejectCurrentWith(new AudioException({
                message: rejectMessage,
            }))
        }
    })

    return current.deferred.promise
}

// Private functions

const activate = () => {
    if (!audioSingleton) {
        return
    }

    audioSingleton.addEventListener('ended', onEnded)
    audioSingleton.addEventListener('error', onError)
}

// Wrapps audio play function in a promise.
const play = () => {
    return Promise.resolve(audioSingleton.play())
}

/**
 * Listener for the onerror event. Rejects the current promise and sets current to null.
 */
const onError = (e) => {
    console.warn('Got error event when playing audio', e)
    new CountMetric(getStoreId(), 'OnError', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
        .count(1)
    rejectCurrentWith(new AudioException({
        message: 'Got an error while playing the audio',
    }))
}

/**
 * Listener to the onended event. Resolves the current promise and sets current to null.
 */
const onEnded = (e) => {
    if (!current) {
        new CountMetric(getStoreId(), 'NoCurrent', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
            .count(1)
        return
    }
    new CountMetric(getStoreId(), 'OnEnded', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
        .count(1)
    current.deferred.resolve()
    current = null
}

/**
 * Rejects the current playing audio promise (if any) and sets current to null. It also emits a metric
 * to track the number of times that an audio request is overriden.
 */
const clearCurrentAndEmitMetricIfNecessary = () => {
    const audioOverriddenMetric = new CountMetric(getStoreId(), 'AudioOverridden', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
    if (!current) {
        audioOverriddenMetric.count(0)
        return
    }

    audioOverriddenMetric.count(1)
    console.warn('Stopping audio for URL', audioSingleton.src)
    rejectCurrentWith(new InterruptedAudioException({
        message: 'New audio with equal or higher priority has requested to play',
    }))
}

/**
 * Rejects the current playing audio promise (if any) with the message passed. Then, sets current to null.
 */
const rejectCurrentWith = (rejection) => {
    if (!current) {
        new CountMetric(undefined, 'NoCurrent', 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource')
            .count(1)
        return
    }
    current.deferred.reject(rejection)
    current = null
}

/**
 * Generates a rejection message based on the Browser error. It also emits metrics for tracking purposes.
 */
const generateBrowserRejectionAndEmitMetrics = (error) => {
    let rejectMessage = error.toString()

    // We validate that DOMException exists because instanceof undefined throws an error
    if (window.DOMException && error) {
        console.warn('Error while playing audio', error.message, error.name)
        rejectMessage = error.name + ' ' + error.message
        new CountMetric(getStoreId(), error.name, 'IhmFlyingDutchman', 'AudioPlayer.playSoundBySource.DOMException')
            .count(1)
    } else {
        console.warn('Error while playing audio', error)
    }

    return rejectMessage
}
